import { sendErrorVolumeBeacon } from './beacon';
import { ERROR_VOLUME_FLUSH_INTERVAL_MS } from './config';
import {
  describeThrown,
  drainErrorVolume,
  isErrorVolumeWindowEmpty,
  recordErrorVolume,
} from './counter';

let installed = false;
let flushTimerId: ReturnType<typeof setInterval> | undefined;

/** Drain and post, but only when the window actually holds something. */
function flushIfNonEmpty(): void {
  if (isErrorVolumeWindowEmpty()) {
    return;
  }
  sendErrorVolumeBeacon(drainErrorVolume());
}

function handleWindowError(event: ErrorEvent): void {
  // event.error is absent for cross-origin script errors ("Script error."),
  // where only the message survives. Count them anyway — an SDK would too.
  const detail = event.error
    ? describeThrown(event.error)
    : `${event.message} @ ${event.filename}:${event.lineno}`;
  recordErrorVolume('uncaught_error', detail);
}

function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  recordErrorVolume('unhandled_rejection', describeThrown(event.reason));
}

function handleVisibilityChange(): void {
  // A backgrounded tab may never come back, so bank the window now. Draining
  // is atomic, so this cannot double-count against the interval flush.
  if (document.visibilityState === 'hidden') {
    flushIfNonEmpty();
  }
}

/**
 * Start counting errors and flushing totals.
 *
 * Idempotent, and listener-only: it observes errors and never intercepts them,
 * so existing behaviour — including whatever surfaces the error to the user —
 * is unchanged.
 *
 * Call once, as early in boot as possible. Errors thrown before this runs are
 * not counted, which biases the numbers slightly low; that is the right
 * direction for a measurement whose purpose is sizing a quota.
 */
export function installErrorVolumeCounting(): void {
  if (installed) {
    return;
  }
  installed = true;

  window.addEventListener('error', handleWindowError);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  flushTimerId = setInterval(flushIfNonEmpty, ERROR_VOLUME_FLUSH_INTERVAL_MS);
}

/** Test-only teardown; production installs once for the page's lifetime. */
export function __uninstallForTesting(): void {
  window.removeEventListener('error', handleWindowError);
  window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  if (flushTimerId !== undefined) {
    clearInterval(flushTimerId);
    flushTimerId = undefined;
  }
  installed = false;
}
